import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { vendasAPI, empresasAPI, produtosAPI, usersAPI } from '@/lib/api';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Eye, Search, Download, Filter, Loader2, Lock, Unlock, ChevronDown, ChevronUp, RotateCcw, Edit, Calendar, ChevronLeft, ChevronRight, Trash2, CheckCircle, AlertTriangle, LayoutList, LayoutGrid, Users, Pin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import UserAvatar from '@/components/UserAvatar';
import ConfirmarSenhaExclusao from '@/components/ConfirmarSenhaExclusao';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CreditoVendaBadge } from '@/components/credito/CreditoVenda';

const STATUS_COLORS_DEFAULT = {
  'Aguardando Viabilidade': '#8b5cf6',
  'Pendência de Instalação': '#eab308',
  'Desabilitada': '#64748b',
  'Cancelamento': '#ef4444',
  'Reprovada': '#be123c',
  'Devolvida': '#f97316',
  'Instalada': '#10b981',
  'Quebra de Instalação': '#dc2626',
  'Desconectada': '#64748b',
  'Auditoria OK': '#06b6d4',
  'Enviado ao BKO': '#3b82f6',
  'Em Tratamento': '#f59e0b',
  'Bloqueada - Duplicidade': '#991b1b',
};

const TIPOS_VENDA = ['Base', 'TT', 'Proposta'];
const TIPOS_SERVICO_MAP = { 'Internet': '🌐 Internet', 'TV': '📺 TV', 'Fixo': '☎️ Fixo', 'Celular': '📱 Celular', 'Combo': '📦 Combo' };
const TIPOS_SERVICO = ['Internet', 'TV', 'Fixo', 'Celular', 'Combo'];
const VENDAS_POR_PAGINA = 10;

// Componente de busca de cidade com autocomplete
const CidadeSearchSelect = ({ cidades, value, onChange, fixadas = [], onToggleFixar }) => {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setAberto(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const normalizar = (t) => (t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

  const filtradasBase = busca
    ? cidades.filter(c => normalizar(c).includes(normalizar(busca)))
    : cidades;
  // fixadas no topo
  const cidadesFiltradas = filtradasBase.slice().sort((a, b) => {
    const fa = fixadas.includes(a) ? 0 : 1;
    const fb = fixadas.includes(b) ? 0 : 1;
    if (fa !== fb) return fa - fb;
    return 0;
  });

  const selecionarCidade = (cidade) => {
    onChange(cidade);
    setAberto(false);
    setBusca('');
  };

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setAberto(!aberto)}
        className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
        data-testid="cidade-select-btn"
      >
        <span className={value === 'all' ? 'text-muted-foreground' : ''}>
          {value === 'all' ? 'Todas as cidades' : value}
        </span>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </button>
      {aberto && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg">
          <div className="p-2 border-b">
            <input
              type="text"
              placeholder="Buscar cidade..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-red-500"
              autoFocus
              data-testid="cidade-search-input"
            />
          </div>
          <div className="max-h-52 overflow-y-auto">
            <button
              type="button"
              onClick={() => selecionarCidade('all')}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${value === 'all' ? 'bg-red-50 text-red-700 font-medium' : ''}`}
            >
              Todas as cidades
            </button>
            {cidadesFiltradas.map(cidade => {
              const isFixada = fixadas.includes(cidade);
              return (
                <div
                  key={cidade}
                  className={`group flex items-center ${value === cidade ? 'bg-red-50' : 'hover:bg-gray-50'}`}
                >
                  {onToggleFixar && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onToggleFixar(cidade); }}
                      className={`pl-2 py-2 shrink-0 transition-colors ${isFixada ? 'text-amber-500' : 'text-gray-300 opacity-0 group-hover:opacity-100 hover:text-gray-500'}`}
                      title={isFixada ? 'Desafixar cidade' : 'Fixar cidade no topo'}
                      data-testid={`pin-cidade-${cidade}`}
                    >
                      <Pin className="h-3.5 w-3.5" fill={isFixada ? 'currentColor' : 'none'} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => selecionarCidade(cidade)}
                    className={`flex-1 text-left px-2 py-2 text-sm ${value === cidade ? 'text-red-700 font-medium' : ''}`}
                    data-testid={`cidade-option-${cidade}`}
                  >
                    {cidade}
                  </button>
                </div>
              );
            })}
            {cidadesFiltradas.length === 0 && (
              <p className="px-3 py-2 text-sm text-gray-400">Nenhuma cidade encontrada</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Vendas = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [vendas, setVendas] = useState([]);
  const [vendasFiltradas, setVendasFiltradas] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [statusCores, setStatusCores] = useState({});
  const [statusAtivos, setStatusAtivos] = useState([]);
  const [statusMap, setStatusMap] = useState({});  // display name -> DB name mapping
  const [statusFixados, setStatusFixados] = useState([]);
  const [statusFixadosLoaded, setStatusFixadosLoaded] = useState(false);
  const [empresasFixadas, setEmpresasFixadas] = useState([]);
  const [empresasFixadasLoaded, setEmpresasFixadasLoaded] = useState(false);
  const [vendedoresFixados, setVendedoresFixados] = useState([]);
  const [cidadesFixadas, setCidadesFixadas] = useState([]);
  const [showEmpresaDropdown, setShowEmpresaDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState({});
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showProdutoDropdown, setShowProdutoDropdown] = useState(false);
  const [showVendaDropdown, setShowVendaDropdown] = useState(false);

  // Fechar dropdowns ao clicar fora
  useEffect(() => {
    if (!showStatusDropdown && !showProdutoDropdown && !showVendaDropdown && !showEmpresaDropdown) return;
    const handler = (e) => {
      if (!e.target.closest('[data-testid="status-multiselect-btn"]') && !e.target.closest('.status-dropdown-list')) {
        setShowStatusDropdown(false);
      }
      if (!e.target.closest('[data-testid="produto-multiselect-btn"]') && !e.target.closest('.produto-dropdown-list')) {
        setShowProdutoDropdown(false);
      }
      if (!e.target.closest('[data-testid="venda-multiselect-btn"]') && !e.target.closest('.venda-dropdown-list')) {
        setShowVendaDropdown(false);
      }
      if (!e.target.closest('[data-testid="empresa-multiselect-btn"]') && !e.target.closest('.empresa-dropdown-list')) {
        setShowEmpresaDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showStatusDropdown, showProdutoDropdown, showVendaDropdown, showEmpresaDropdown]);
  const [exibirInativos, setExibirInativos] = useState(false);
  const [periodoSelecionado, setPeriodoSelecionado] = useState('mes_atual');
  
  // Paginação
  const [paginaAtual, setPaginaAtual] = useState(1);
  
  // Layout (lista ou grid)
  const [layoutMode, setLayoutMode] = useState('lista');
  
  // Modal de Importação
  
  // Filtros expandidos
  const [filtros, setFiltros] = useState({
    tipo_servico: [],
    tipo_venda: [],
    cpf: '',
    cnpj: '',
    cidade: 'all',
    uf: 'all',
    status: [],
    empresas: [],
    colaborador_id: [],
    cliente: '',
    contrato: '',
    proposta: '',
    codigo: '',
    data_inicial: '',
    data_final: '',
    busca_geral: '',
  });

  const [colabAberto, setColabAberto] = useState(() => {
    try { return localStorage.getItem('colab_aberto') !== 'false'; } catch { return true; }
  });
  const [filtrosAberto, setFiltrosAberto] = useState(() => {
    try { return localStorage.getItem('filtros_aberto') !== 'false'; } catch { return true; }
  });

  const toggleColab = () => {
    setColabAberto(prev => {
      const next = !prev;
      try { localStorage.setItem('colab_aberto', String(next)); } catch {}
      return next;
    });
  };

  const toggleFiltros = () => {
    setFiltrosAberto(prev => {
      const next = !prev;
      try { localStorage.setItem('filtros_aberto', String(next)); } catch {}
      return next;
    });
  };

  // Lista de cidades únicas
  const [cidades, setCidades] = useState([]);

  // Toggle fixar/desfixar status
  const toggleFixarStatus = async (statusNome) => {
    const isFixado = statusFixados.includes(statusNome);
    const novaLista = isFixado
      ? statusFixados.filter(s => s !== statusNome)
      : [...statusFixados, statusNome];
    setStatusFixados(novaLista);
    try {
      await api.put('/users/me/status-fixados', { status_fixados: novaLista });
      toast.success(isFixado ? `"${statusNome}" removido dos fixados` : `"${statusNome}" fixado!`);
    } catch {
      toast.error('Erro ao salvar status fixados');
      setStatusFixados(statusFixados); // rollback
    }
  };

  // Toggle fixar/desfixar empresa
  const toggleFixarEmpresa = async (empresaId, empresaNome) => {
    const isFixada = empresasFixadas.includes(empresaId);
    const novaLista = isFixada
      ? empresasFixadas.filter(e => e !== empresaId)
      : [...empresasFixadas, empresaId];
    setEmpresasFixadas(novaLista);
    try {
      await api.put('/users/me/empresas-fixadas', { empresas_fixadas: novaLista });
      toast.success(isFixada ? `"${empresaNome}" removida das fixadas` : `"${empresaNome}" fixada!`);
    } catch {
      toast.error('Erro ao salvar empresas fixadas');
      setEmpresasFixadas(empresasFixadas); // rollback
    }
  };
  const toggleFixarVendedor = async (vendedorId, vendedorNome) => {
    const isFixado = vendedoresFixados.includes(vendedorId);
    const novaLista = isFixado
      ? vendedoresFixados.filter(v => v !== vendedorId)
      : [...vendedoresFixados, vendedorId];
    setVendedoresFixados(novaLista);
    try {
      await api.put('/users/me/vendedores-fixados', { vendedores_fixados: novaLista });
      toast.success(isFixado ? `"${vendedorNome}" removido dos fixados` : `"${vendedorNome}" fixado!`);
    } catch {
      toast.error('Erro ao salvar vendedores fixados');
      setVendedoresFixados(vendedoresFixados);
    }
  };
  const toggleFixarCidade = async (cidade) => {
    const isFixada = cidadesFixadas.includes(cidade);
    const novaLista = isFixada
      ? cidadesFixadas.filter(c => c !== cidade)
      : [...cidadesFixadas, cidade];
    setCidadesFixadas(novaLista);
    try {
      await api.put('/users/me/cidades-fixadas', { cidades_fixadas: novaLista });
      toast.success(isFixada ? `"${cidade}" removida das fixadas` : `"${cidade}" fixada!`);
    } catch {
      toast.error('Erro ao salvar cidades fixadas');
      setCidadesFixadas(cidadesFixadas);
    }
  };
  const [ufs, setUfs] = useState([]);

  useEffect(() => {
    loadData(periodoSelecionado);
  }, []);

  // Recarregar quando periodo muda
  useEffect(() => {
    if (vendas.length > 0 || !loading) {
      loadData(periodoSelecionado);
    }
    setPaginaAtual(1);
  }, [periodoSelecionado]);

  // Resetar pagina quando filtros mudam
  useEffect(() => {
    setPaginaAtual(1);
  }, [filtros]);

  // Busca em tempo real: aplica filtros locais quando busca_geral, cidade, status, etc. mudam
  useEffect(() => {
    if (vendas.length > 0) {
      aplicarFiltros();
    }
  }, [filtros.busca_geral, filtros.cidade, filtros.uf, filtros.status, filtros.tipo_venda, filtros.tipo_servico, filtros.empresas, filtros.colaborador_id, filtros.cpf, filtros.cnpj]); // eslint-disable-line

  // Calcula params de data para enviar ao servidor
  const getDateParams = (periodo, customDates = {}) => {
    if (customDates.data_inicial || customDates.data_final) {
      const params = {};
      if (customDates.data_inicial) params.data_inicio = customDates.data_inicial + 'T00:00:00';
      if (customDates.data_final) params.data_fim = customDates.data_final + 'T23:59:59';
      return params;
    }
    const hoje = new Date();
    switch (periodo) {
      case 'hoje': {
        const hojeStr = hoje.toISOString().split('T')[0];
        return { data_inicio: hojeStr + 'T00:00:00', data_fim: hojeStr + 'T23:59:59' };
      }
      case 'semana': {
        const diaSemana = hoje.getDay();
        const seg = new Date(hoje);
        seg.setDate(hoje.getDate() - (diaSemana === 0 ? 6 : diaSemana - 1));
        const dom = new Date(seg);
        dom.setDate(seg.getDate() + 6);
        return { data_inicio: seg.toISOString().split('T')[0] + 'T00:00:00', data_fim: dom.toISOString().split('T')[0] + 'T23:59:59' };
      }
      case 'mes_atual':
        return { data_inicio: startOfMonth(hoje).toISOString(), data_fim: endOfMonth(hoje).toISOString() };
      case 'mes_anterior': {
        const m = subMonths(hoje, 1);
        return { data_inicio: startOfMonth(m).toISOString(), data_fim: endOfMonth(m).toISOString() };
      }
      case 'ultimos_3_meses':
        return { data_inicio: startOfMonth(subMonths(hoje, 2)).toISOString(), data_fim: endOfMonth(hoje).toISOString() };
      case 'todos':
        return {};
      default:
        return { data_inicio: startOfMonth(hoje).toISOString(), data_fim: endOfMonth(hoje).toISOString() };
    }
  };

  const loadData = async (periodo = 'mes_atual', customDates = {}) => {
    setLoading(true);
    try {
      const dateParams = getDateParams(periodo, customDates);
      const vendasRes = await vendasAPI.list(dateParams);
      const todasVendas = vendasRes.data;
      setVendas(todasVendas);
      // Aplicar filtros locais ativos sobre os dados carregados
      const filtradas = aplicarFiltrosLocal(todasVendas);
      setVendasFiltradas(filtradas);
      setLoading(false);

      // Carregar dados auxiliares em background
      const [empresasRes, produtosRes, usersRes, statusRes, fixadosRes, empFixadasRes, vendFixadosRes, cidFixadasRes] = await Promise.all([
        empresasAPI.list(),
        produtosAPI.list(),
        usersAPI.listVendedores().catch(() => ({ data: [] })),
        api.get('/config/status-venda').catch(() => ({ data: [] })),
        api.get('/users/me/status-fixados').catch(() => ({ data: [] })),
        api.get('/users/me/empresas-fixadas').catch(() => ({ data: [] })),
        api.get('/users/me/vendedores-fixados').catch(() => ({ data: [] })),
        api.get('/users/me/cidades-fixadas').catch(() => ({ data: [] }))
      ]);
      
      setEmpresas(empresasRes.data);
      setProdutos(produtosRes.data);
      setUsuarios(usersRes.data || []);
      setVendedoresFixados(Array.isArray(vendFixadosRes.data) ? vendFixadosRes.data : []);
      setCidadesFixadas(Array.isArray(cidFixadasRes.data) ? cidFixadasRes.data : []);

      // Carregar status fixados do usuário
      const fixados = Array.isArray(fixadosRes.data) ? fixadosRes.data : [];
      setStatusFixados(fixados);

      // Carregar empresas fixadas do usuário (só as que ainda existem)
      const idsEmpresas = new Set((empresasRes.data || []).map(e => e.id));
      const empFixadas = (Array.isArray(empFixadasRes.data) ? empFixadasRes.data : []).filter(id => idsEmpresas.has(id));
      setEmpresasFixadas(empFixadas);
      if (empFixadas.length > 0 && !empresasFixadasLoaded) {
        setEmpresasFixadasLoaded(true);
        setFiltros(prev => prev.empresas.length === 0 ? { ...prev, empresas: [...empFixadas] } : prev);
      }

      const cores = { ...STATUS_COLORS_DEFAULT };
      const ativos = [];
      const mapDisplayToDb = {};
      if (statusRes.data) {
        statusRes.data.forEach(s => {
          if (s.cor) {
            cores[s.nome] = s.cor;
            if (s.nome_original) cores[s.nome_original] = s.cor;
          }
          if (s.ativo !== false) {
            ativos.push(s.nome);
          }
          // Mapeamento: nome display → nome no banco
          if (s.nome_original && s.nome_original !== s.nome) {
            mapDisplayToDb[s.nome] = s.nome_original;
          }
        });
      }
      setStatusCores(cores);
      setStatusAtivos(ativos);
      setStatusMap(mapDisplayToDb);

      // Auto-aplicar status fixados se o usuário ainda não selecionou nada
      if (fixados.length > 0 && !statusFixadosLoaded) {
        setStatusFixadosLoaded(true);
        setFiltros(prev => prev.status.length === 0 ? { ...prev, status: [...fixados] } : prev);
      }
      
      const normalizarTexto = (texto) => {
        return texto?.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase().trim() || '';
      };
      
      const cidadesMap = new Map();
      todasVendas.forEach(v => {
        const cidade = v.endereco?.cidade?.toUpperCase().trim();
        const uf = v.endereco?.estado?.toUpperCase().trim() || '';
        if (cidade) {
          const cidadeNormalizada = normalizarTexto(cidade);
          const cidadeComUF = uf ? `${cidade}/${uf}` : cidade;
          if (!cidadesMap.has(cidadeNormalizada) || cidadeComUF.length > cidadesMap.get(cidadeNormalizada).length) {
            cidadesMap.set(cidadeNormalizada, cidadeComUF);
          }
        }
      });
      const cidadesUnicas = [...cidadesMap.values()].sort((a, b) => a.localeCompare(b, 'pt-BR'));
      setCidades(cidadesUnicas);
      // Extrair UFs únicos
      const ufsSet = new Set();
      todasVendas.forEach(v => {
        const uf = v.endereco?.estado?.toUpperCase().trim();
        if (uf) ufsSet.add(uf);
      });
      setUfs([...ufsSet].sort());
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
      setLoading(false);
    }
  };

  // Consultar: recarrega do servidor (com datas) e aplica filtros locais
  const consultarVendas = async () => {
    setLoading(true);
    try {
      let dateParams;
      if (filtros.data_inicial || filtros.data_final) {
        dateParams = getDateParams(periodoSelecionado, { data_inicial: filtros.data_inicial, data_final: filtros.data_final });
      } else {
        dateParams = getDateParams(periodoSelecionado);
      }
      const vendasRes = await vendasAPI.list(dateParams);
      const todasVendas = vendasRes.data;
      setVendas(todasVendas);

      // Aplicar filtros locais sobre os dados carregados
      const filtradas = aplicarFiltrosLocal(todasVendas);
      setVendasFiltradas(filtradas);
      setPaginaAtual(1);
    } catch (e) {
      console.error('Erro ao consultar vendas:', e);
    } finally {
      setLoading(false);
    }
  };

  // Aplica filtros locais (sem chamar API)
  const aplicarFiltrosLocal = (dados) => {
    const normalizar = (texto) => {
      return (texto || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
    };

    let resultado = [...dados];

    // Filtro por tipo de venda (multi-select)
    if (filtros.tipo_venda.length > 0) {
      resultado = resultado.filter(v => filtros.tipo_venda.includes(v.tipo_venda));
    }

    // Filtro por tipo de servico/produto (multi-select: TV, Internet, Fixo, Celular, Combo)
    if (filtros.tipo_servico.length > 0) {
      resultado = resultado.filter(v => {
        const produtosVenda = (v.produtos_vendidos || '').toLowerCase();
        return filtros.tipo_servico.some(tipo => {
          const t = tipo.toLowerCase();
          if (produtosVenda.includes(t)) return true;
          if (t === 'tv' && v.plano_tv_nome) return true;
          if (t === 'internet' && v.plano_internet_nome) return true;
          if (t === 'fixo' && v.plano_fixo_nome) return true;
          if ((t === 'celular' || t === 'movel') && v.plano_movel_nome) return true;
          if (t === 'combo' && produtosVenda.includes('combo')) return true;
          return false;
        });
      });
    }

    // Filtro por CPF
    if (filtros.cpf) {
      resultado = resultado.filter(v => 
        v.cliente?.documento?.replace(/\D/g, '').includes(filtros.cpf.replace(/\D/g, ''))
      );
    }

    // Filtro por CNPJ
    if (filtros.cnpj) {
      resultado = resultado.filter(v => 
        v.cliente?.documento?.replace(/\D/g, '').includes(filtros.cnpj.replace(/\D/g, ''))
      );
    }

    // Filtro por cidade (insensível a acentos) - extrai cidade sem UF
    if (filtros.cidade !== 'all') {
      const cidadeSemUF = filtros.cidade.split('/')[0];
      const cidadeNorm = normalizar(cidadeSemUF);
      resultado = resultado.filter(v => normalizar(v.endereco?.cidade) === cidadeNorm);
    }

    // Filtro por UF
    if (filtros.uf !== 'all') {
      resultado = resultado.filter(v => (v.endereco?.estado || '').toUpperCase().trim() === filtros.uf);
    }

    // Filtro por status (multi-select, considerando nomes customizados)
    if (filtros.status.length > 0) {
      resultado = resultado.filter(v => {
        const vendaStatus = v.status?.toLowerCase();
        return filtros.status.some(s => {
          const statusDbNome = statusMap[s] || s;
          return vendaStatus === s.toLowerCase() || vendaStatus === statusDbNome.toLowerCase();
        });
      });
    }

    // Filtro por empresa (multi-select)
    if (filtros.empresas.length > 0) {
      resultado = resultado.filter(v => filtros.empresas.includes(v.empresa_id));
    }

    // Filtro por colaborador/vendedor (multi-select)
    if (filtros.colaborador_id.length > 0) {
      resultado = resultado.filter(v => filtros.colaborador_id.includes(v.vendedor_id));
    }

    // Busca geral unificada (cliente, nome, CPF, CNPJ, contrato, código) — insensível a acentos
    if (filtros.busca_geral) {
      const termoNorm = normalizar(filtros.busca_geral);
      const termoDigitos = filtros.busca_geral.replace(/\D/g, '');
      resultado = resultado.filter(v => {
        const nomeCliente = normalizar(v.cliente?.nome_razao_social);
        const documento = v.cliente?.documento?.replace(/\D/g, '') || '';
        const contrato = normalizar(v.contrato);
        const protocolo = normalizar(v.protocolo);
        const codigo = normalizar(v.id);
        const telefone1 = v.cliente?.telefone1?.replace(/\D/g, '') || '';
        const telefone2 = v.cliente?.telefone2?.replace(/\D/g, '') || '';
        const telefonevenda = v.telefone_venda?.replace(/\D/g, '') || '';
        return nomeCliente.includes(termoNorm) ||
               (termoDigitos && documento.includes(termoDigitos)) ||
               contrato.includes(termoNorm) ||
               protocolo.includes(termoNorm) ||
               codigo.includes(termoNorm) ||
               (termoDigitos && telefone1.includes(termoDigitos)) ||
               (termoDigitos && telefone2.includes(termoDigitos)) ||
               (termoDigitos && telefonevenda.includes(termoDigitos));
      });
    }

    // Filtro por nome do cliente (legado)
    if (filtros.cliente) {
      const termoNorm = normalizar(filtros.cliente);
      resultado = resultado.filter(v => 
        normalizar(v.cliente?.nome_razao_social).includes(termoNorm)
      );
    }

    // Filtro por número do contrato (legado)
    if (filtros.contrato) {
      const termoNorm = normalizar(filtros.contrato);
      resultado = resultado.filter(v => 
        normalizar(v.contrato).includes(termoNorm)
      );
    }

    // Filtro por proposta (legado)
    if (filtros.proposta) {
      const termoNorm = normalizar(filtros.proposta);
      resultado = resultado.filter(v => 
        normalizar(v.protocolo).includes(termoNorm)
      );
    }

    // Filtro por código (legado)
    if (filtros.codigo) {
      const termoNorm = normalizar(filtros.codigo);
      resultado = resultado.filter(v => 
        normalizar(v.protocolo).includes(termoNorm)
      );
    }

    return resultado;
  };

  // Aplicar filtros sobre vendas já carregadas (sem recarregar do servidor)
  const aplicarFiltros = () => {
    const filtradas = aplicarFiltrosLocal(vendas);
    setVendasFiltradas(filtradas);
    setPaginaAtual(1);
  };

  const limparFiltros = () => {
    setFiltros({
      tipo_servico: [],
      tipo_venda: [],
      cpf: '',
      cnpj: '',
      cidade: 'all',
      uf: 'all',
      status: [],
      empresas: [],
      colaborador_id: [],
      cliente: '',
      contrato: '',
      proposta: '',
      codigo: '',
      data_inicial: '',
      data_final: '',
      busca_geral: '',
    });
    setPeriodoSelecionado('mes_atual');
    loadData('mes_atual');
  };

  const toggleRow = (vendaId) => {
    setExpandedRows(prev => ({
      ...prev,
      [vendaId]: !prev[vendaId]
    }));
  };

  const handleBloquear = async (vendaId) => {
    try {
      await vendasAPI.bloquear(vendaId);
      toast.success('Venda bloqueada para tratamento');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao bloquear venda');
    }
  };

  const handleDesbloquear = async (vendaId) => {
    try {
      await vendasAPI.desbloquear(vendaId);
      toast.success('Venda desbloqueada');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao desbloquear venda');
    }
  };

  const handleExportar = async () => {
    try {
      // Montar lista de status considerando mapeamento display → DB
      let statusFiltro = [];
      if (filtros.status.length > 0) {
        filtros.status.forEach(s => {
          statusFiltro.push(s);
          const dbNome = statusMap[s];
          if (dbNome && dbNome !== s) {
            statusFiltro.push(dbNome);
          }
        });
      }
      
      const filtrosLimpos = {
        status: statusFiltro,
        vendedor_id: filtros.colaborador_id.length === 1 ? filtros.colaborador_id[0] : '',
        empresa_id: filtros.empresas.length === 1 ? filtros.empresas[0] : '',
        empresa_ids: filtros.empresas.length > 1 ? filtros.empresas : null,
        data_inicial: filtros.data_inicial || '',
        data_final: filtros.data_final || '',
        periodo: (!filtros.data_inicial && !filtros.data_final) ? periodoSelecionado : '',
        tipo_venda: filtros.tipo_venda.length > 0 ? filtros.tipo_venda[0] : '',
        cidade: filtros.cidade === 'all' ? '' : filtros.cidade,
      };
      
      const response = await vendasAPI.exportar(filtrosLimpos);
      const url = window.URL.createObjectURL(new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vendas_${new Date().getTime()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Vendas exportadas com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar:', error);
      toast.error('Erro ao exportar vendas');
    }
  };

  // Excluir venda (apenas ADMIN) — abre modal que exige a senha do admin
  const [exclusaoPendente, setExclusaoPendente] = useState(null); // { id, protocolo }
  const [excluindo, setExcluindo] = useState(false);

  const handleExcluir = (vendaId, protocolo) => {
    setExclusaoPendente({ id: vendaId, protocolo });
  };

  const confirmarExclusao = async (senha) => {
    if (!exclusaoPendente) return;
    setExcluindo(true);
    try {
      await vendasAPI.delete(exclusaoPendente.id, senha);
      toast.success(`Venda ${exclusaoPendente.protocolo} excluída com sucesso`);
      setExclusaoPendente(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir venda');
    } finally {
      setExcluindo(false);
    }
  };

  // Liberar venda bloqueada por duplicidade (BKO/Admin)
  const handleLiberarDuplicidade = async (vendaId, protocolo) => {
    if (!window.confirm(`Liberar a venda ${protocolo}?\n\nA venda será desbloqueada e enviada para o BKO.`)) {
      return;
    }
    
    try {
      await vendasAPI.liberarDuplicidade(vendaId);
      toast.success(`Venda ${protocolo} liberada com sucesso`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao liberar venda');
    }
  };

  const getProdutosNomes = (venda) => {
    const emojis = [];
    const emojiMap = { tv: '📺', internet: '🌐', fixo: '☎️', movel: '📱', alacarte: '📺', pontosextra: '📺', pontosadicionais: '📺' };
    const planoKeys = ['tv', 'internet', 'fixo', 'movel', 'alacarte', 'pontosextra', 'pontosadicionais'];
    for (const key of planoKeys) {
      const nome = venda[`plano_${key}_nome`];
      if (nome) {
        const emoji = emojiMap[key];
        if (emoji && !emojis.includes(emoji)) emojis.push(emoji);
      }
    }
    if (emojis.length > 0) return emojis.join(' ');
    if (venda.produtos && venda.produtos.length > 0) return '📦';
    return '-';
  };

  // Função para mascarar contrato (Vendedor vê mascarado, BKO/Admin vê completo)
  const mascararContrato = (contrato) => {
    if (!contrato) return '-';
    
    // Converter para string e remover ".0" se existir (quando vem como número)
    let contratoStr = String(contrato);
    if (contratoStr.endsWith('.0')) {
      contratoStr = contratoStr.slice(0, -2);
    }
    
    // Se for Admin ou BKO, mostra completo
    if (isAdmin || isBKO) return contratoStr;
    // Se for Vendedor, mascara mostrando apenas últimos 4 dígitos
    if (contratoStr.length <= 4) return contratoStr;
    return '****' + contratoStr.slice(-4);
  };

  const podeBloquear = user?.perfil === 'ADMIN' || user?.perfil === 'BKO' || user?.perfil === 'BKO_MAILING';
  const isAdmin = user?.perfil === 'ADMIN';
  const isBKO = user?.perfil === 'BKO' || user?.perfil === 'BKO_MAILING';
  const isSupervisor = user?.perfil === 'SUPERVISOR';
  const podeVerColaboradores = isAdmin || isBKO || isSupervisor;

  // Filtrar colaboradores por ativo/inativo + fixados no topo
  const colaboradoresFiltrados = (exibirInativos 
    ? usuarios 
    : usuarios.filter(u => u.ativo)
  ).slice().sort((a, b) => {
    const fa = vendedoresFixados.includes(a.id) ? 0 : 1;
    const fb = vendedoresFixados.includes(b.id) ? 0 : 1;
    if (fa !== fb) return fa - fb;
    return (a.nome || '').localeCompare(b.nome || '', 'pt-BR');
  });

  return (
    <div className="space-y-6">
      {/* Header com info do usuário */}
      <div className="bg-gray-100 p-2 rounded-lg text-sm text-gray-600 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span>⏰ {format(new Date(), "dd/MM/yy HH:mm:ss")}</span>
          <span>👤 {user?.nome} | {user?.perfil}</span>
          <span>📧 {user?.email}</span>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Botões de Layout */}
          <div className="flex items-center gap-1 bg-white rounded-lg p-1 shadow-sm">
            <Button
              variant={layoutMode === 'lista' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setLayoutMode('lista')}
              className={`h-8 px-3 ${layoutMode === 'lista' ? 'bg-red-600 hover:bg-red-700' : ''}`}
              title="Layout Lista"
            >
              <LayoutList className="h-4 w-4 mr-1" />
              Lista
            </Button>
            <Button
              variant={layoutMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setLayoutMode('grid')}
              className={`h-8 px-3 ${layoutMode === 'grid' ? 'bg-red-600 hover:bg-red-700' : ''}`}
              title="Layout em Blocos"
            >
              <LayoutGrid className="h-4 w-4 mr-1" />
              Blocos
            </Button>
          </div>

          {/* Filtros de Data */}
          <div className="flex items-center gap-3 bg-gradient-to-r from-red-600 to-purple-600 text-white px-4 py-1.5 rounded-lg">
            <Calendar className="w-4 h-4" />
            <span className="text-sm font-medium">Período:</span>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={filtros.data_inicial}
                onChange={(e) => setFiltros({ ...filtros, data_inicial: e.target.value })}
                className="bg-red-700 text-white border-none text-sm rounded px-2 py-0.5 cursor-pointer"
                data-testid="data-inicial-input"
              />
              <span className="text-sm">até</span>
              <input
                type="date"
                value={filtros.data_final}
                onChange={(e) => setFiltros({ ...filtros, data_final: e.target.value })}
                className="bg-red-700 text-white border-none text-sm rounded px-2 py-0.5 cursor-pointer"
                data-testid="data-final-input"
              />
              {(filtros.data_inicial || filtros.data_final) && (
                <button
                  onClick={consultarVendas}
                  className="bg-white text-red-700 text-xs font-bold px-3 py-1 rounded hover:bg-red-50 transition-colors"
                  data-testid="buscar-datas-btn"
                >
                  Buscar
                </button>
              )}
            </div>
            <select
              value={periodoSelecionado}
              onChange={(e) => {
                setPeriodoSelecionado(e.target.value);
                setFiltros(prev => ({ ...prev, data_inicial: '', data_final: '' }));
              }}
              className="bg-red-700 text-white border-none text-sm rounded px-2 py-0.5 cursor-pointer ml-2"
              data-testid="periodo-select"
            >
              <option value="hoje">Hoje</option>
              <option value="semana">Esta Semana</option>
              <option value="mes_atual">Mês Atual</option>
              <option value="mes_anterior">Mês Anterior</option>
              <option value="ultimos_3_meses">Últimos 3 Meses</option>
              <option value="todos">Todos</option>
            </select>
          </div>
        </div>
      </div>

      {/* Filtros Expandidos */}
      <Card>
        <CardContent className="pt-6">

          {/* Toggle Filtros */}
          <button
            onClick={toggleFiltros}
            className="w-full flex items-center justify-between mb-3 group cursor-pointer"
          >
            <span className="text-sm font-semibold text-red-600 flex items-center gap-1.5">
              <Filter size={14} /> Filtros Avancados
              {(filtros.tipo_servico.length > 0 || filtros.tipo_venda.length > 0 || filtros.cidade || filtros.status.length > 0 || filtros.empresas.length > 0) && (
                <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 text-[10px] px-1.5 py-0">
                  Ativo
                </Badge>
              )}
            </span>
            {filtrosAberto ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
          </button>

          {filtrosAberto && (
            <>
          {/* Linha 1 - Tipo de Serviço, Tipo da Venda, CPF, CNPJ, Cidade/Distrito */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <Label className="text-red-600 text-sm">Produto</Label>
              <div className="mt-1 relative">
                <button
                  type="button"
                  onClick={() => setShowProdutoDropdown(!showProdutoDropdown)}
                  className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  data-testid="produto-multiselect-btn"
                >
                  <span className="truncate">
                    {filtros.tipo_servico.length === 0 ? 'Selecione' : `${filtros.tipo_servico.join(', ')}`}
                  </span>
                  <svg className="h-4 w-4 opacity-50" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
                {showProdutoDropdown && (
                  <div className="absolute z-50 mt-1 w-full max-h-52 overflow-y-auto rounded-md border bg-popover p-2 shadow-md produto-dropdown-list">
                    {filtros.tipo_servico.length > 0 && (
                      <button type="button" onClick={() => setFiltros({ ...filtros, tipo_servico: [] })} className="w-full text-left text-xs text-red-400 hover:text-red-300 mb-1 px-2 py-1">
                        Limpar selecao
                      </button>
                    )}
                    {TIPOS_SERVICO.map((tipo) => (
                      <label key={tipo} className="flex items-center gap-2 px-2 py-1.5 hover:bg-accent rounded cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={filtros.tipo_servico.includes(tipo)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFiltros({ ...filtros, tipo_servico: [...filtros.tipo_servico, tipo] });
                            } else {
                              setFiltros({ ...filtros, tipo_servico: filtros.tipo_servico.filter(s => s !== tipo) });
                            }
                          }}
                          className="rounded border-gray-400"
                        />
                        {TIPOS_SERVICO_MAP[tipo] || tipo}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label className="text-red-600 text-sm">Tipo da Venda</Label>
              <div className="mt-1 relative">
                <button
                  type="button"
                  onClick={() => setShowVendaDropdown(!showVendaDropdown)}
                  className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  data-testid="venda-multiselect-btn"
                >
                  <span className="truncate">
                    {filtros.tipo_venda.length === 0 ? 'Selecione' : `${filtros.tipo_venda.join(', ')}`}
                  </span>
                  <svg className="h-4 w-4 opacity-50" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
                {showVendaDropdown && (
                  <div className="absolute z-50 mt-1 w-full max-h-52 overflow-y-auto rounded-md border bg-popover p-2 shadow-md venda-dropdown-list">
                    {filtros.tipo_venda.length > 0 && (
                      <button type="button" onClick={() => setFiltros({ ...filtros, tipo_venda: [] })} className="w-full text-left text-xs text-red-400 hover:text-red-300 mb-1 px-2 py-1">
                        Limpar selecao
                      </button>
                    )}
                    {TIPOS_VENDA.map((tipo) => (
                      <label key={tipo} className="flex items-center gap-2 px-2 py-1.5 hover:bg-accent rounded cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={filtros.tipo_venda.includes(tipo)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFiltros({ ...filtros, tipo_venda: [...filtros.tipo_venda, tipo] });
                            } else {
                              setFiltros({ ...filtros, tipo_venda: filtros.tipo_venda.filter(s => s !== tipo) });
                            }
                          }}
                          className="rounded border-gray-400"
                        />
                        {tipo}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label className="text-red-600 text-sm">Cidade/Distrito</Label>
              <div className="mt-1 relative">
                <CidadeSearchSelect
                  cidades={cidades}
                  value={filtros.cidade}
                  onChange={(value) => setFiltros({ ...filtros, cidade: value })}
                  fixadas={cidadesFixadas}
                  onToggleFixar={toggleFixarCidade}
                />
              </div>
            </div>

            <div>
              <Label className="text-red-600 text-sm">UF</Label>
              <div className="mt-1">
                <Select value={filtros.uf} onValueChange={(value) => setFiltros({ ...filtros, uf: value })}>
                  <SelectTrigger data-testid="uf-select">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas UFs</SelectItem>
                    {ufs.map(uf => (
                      <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Linha 2 - Situações e Empresa */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="text-red-600 text-sm">Situacoes</Label>
              <div className="mt-1 relative">
                <button
                  type="button"
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  data-testid="status-multiselect-btn"
                >
                  <span className="truncate">
                    {filtros.status.length === 0
                      ? (statusFixados.length > 0 ? `${statusFixados.length} fixado(s)` : 'Selecione')
                      : `${filtros.status.length} selecionado(s)`}
                  </span>
                  <svg className="h-4 w-4 opacity-50" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
                {showStatusDropdown && (
                  <div className="absolute z-50 mt-1 w-full max-h-52 overflow-y-auto rounded-md border bg-popover p-2 shadow-md status-dropdown-list">
                    <div className="flex items-center justify-between mb-1 px-2">
                      {filtros.status.length > 0 && (
                        <button type="button" onClick={() => setFiltros({ ...filtros, status: [] })} className="text-xs text-red-400 hover:text-red-300 py-1">
                          Limpar selecao
                        </button>
                      )}
                      {statusFixados.length > 0 && (
                        <button type="button" onClick={() => setFiltros({ ...filtros, status: [...statusFixados] })} className="text-xs text-amber-400 hover:text-amber-300 py-1 ml-auto flex items-center gap-1">
                          <Pin className="h-3 w-3" /> Aplicar fixados
                        </button>
                      )}
                    </div>
                    {(statusAtivos.length > 0 ? statusAtivos : Object.keys(STATUS_COLORS_DEFAULT)).map((status) => (
                      <div key={status} className="flex items-center gap-2 px-2 py-1.5 hover:bg-accent rounded text-sm group">
                        <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                          <input
                            type="checkbox"
                            checked={filtros.status.includes(status)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFiltros({ ...filtros, status: [...filtros.status, status] });
                              } else {
                                setFiltros({ ...filtros, status: filtros.status.filter(s => s !== status) });
                              }
                            }}
                            className="rounded border-gray-400"
                          />
                          <span className="truncate">{status}</span>
                        </label>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleFixarStatus(status); }}
                          className={`p-0.5 rounded transition-colors shrink-0 ${statusFixados.includes(status) ? 'text-amber-400' : 'text-gray-400 opacity-0 group-hover:opacity-100'}`}
                          title={statusFixados.includes(status) ? 'Remover dos fixados' : 'Fixar status'}
                          data-testid={`pin-status-${status.replace(/\s+/g, '-').toLowerCase()}`}
                        >
                          <Pin className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label className="text-red-600 text-sm">Empresa</Label>
              <div className="mt-1 relative">
                <button
                  type="button"
                  onClick={() => setShowEmpresaDropdown(!showEmpresaDropdown)}
                  className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  data-testid="empresa-multiselect-btn"
                >
                  <span className="truncate">
                    {filtros.empresas.length === 0
                      ? (empresasFixadas.length > 0 ? `${empresasFixadas.length} fixada(s)` : 'Selecione')
                      : filtros.empresas.length === 1
                        ? (empresas.find(e => e.id === filtros.empresas[0])?.nome || '1 selecionada')
                        : `${filtros.empresas.length} selecionada(s)`}
                  </span>
                  <svg className="h-4 w-4 opacity-50" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
                {showEmpresaDropdown && (
                  <div className="absolute z-50 mt-1 w-full max-h-52 overflow-y-auto rounded-md border bg-popover p-2 shadow-md empresa-dropdown-list" data-testid="empresa-dropdown-list">
                    <div className="flex items-center justify-between mb-1 px-2">
                      {filtros.empresas.length > 0 && (
                        <button type="button" onClick={() => setFiltros({ ...filtros, empresas: [] })} className="text-xs text-red-400 hover:text-red-300 py-1" data-testid="empresa-limpar-selecao">
                          Limpar selecao
                        </button>
                      )}
                      {empresasFixadas.length > 0 && (
                        <button type="button" onClick={() => setFiltros({ ...filtros, empresas: [...empresasFixadas] })} className="text-xs text-amber-400 hover:text-amber-300 py-1 ml-auto flex items-center gap-1" data-testid="empresa-aplicar-fixadas">
                          <Pin className="h-3 w-3" /> Aplicar fixadas
                        </button>
                      )}
                    </div>
                    {empresas.filter(e => e.ativo !== false).map((empresa) => (
                      <div key={empresa.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-accent rounded text-sm group">
                        <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                          <input
                            type="checkbox"
                            checked={filtros.empresas.includes(empresa.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFiltros({ ...filtros, empresas: [...filtros.empresas, empresa.id] });
                              } else {
                                setFiltros({ ...filtros, empresas: filtros.empresas.filter(id => id !== empresa.id) });
                              }
                            }}
                            className="rounded border-gray-400"
                            data-testid={`empresa-check-${empresa.id}`}
                          />
                          <span className="truncate">{empresa.nome}</span>
                        </label>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleFixarEmpresa(empresa.id, empresa.nome); }}
                          className={`p-0.5 rounded transition-colors shrink-0 ${empresasFixadas.includes(empresa.id) ? 'text-amber-400' : 'text-gray-400 opacity-0 group-hover:opacity-100'}`}
                          title={empresasFixadas.includes(empresa.id) ? 'Remover das fixadas' : 'Fixar empresa'}
                          data-testid={`pin-empresa-${empresa.id}`}
                        >
                          <Pin className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
            </>
          )}

          {/* Linha 3 - Colaboradores (Admin/BKO/Supervisor) */}
          {podeVerColaboradores && (
            <div className="mb-4">
              <button
                onClick={toggleColab}
                className="w-full flex items-center justify-between mb-2 group cursor-pointer"
              >
                <Label className="text-red-600 text-sm flex items-center gap-1.5 cursor-pointer">
                  <Users size={14} /> Colaboradores
                  {filtros.colaborador_id.length > 0 && (
                    <Badge className="ml-1 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 text-[10px] px-1.5 py-0">
                      {filtros.colaborador_id.length} selecionado{filtros.colaborador_id.length > 1 ? 's' : ''}
                    </Badge>
                  )}
                </Label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const allIds = colaboradoresFiltrados.map(u => u.id);
                      const allSelected = allIds.length > 0 && allIds.every(id => filtros.colaborador_id.includes(id));
                      setFiltros({ ...filtros, colaborador_id: allSelected ? [] : allIds });
                    }}
                    className="text-xs text-red-500 hover:text-red-400 font-medium"
                  >
                    {colaboradoresFiltrados.length > 0 && colaboradoresFiltrados.every(u => filtros.colaborador_id.includes(u.id)) ? 'Nenhum' : 'Todos'}
                  </button>
                  <label className="flex items-center gap-1.5 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      id="exibirInativos"
                      checked={exibirInativos}
                      onCheckedChange={setExibirInativos}
                      className="h-3.5 w-3.5"
                    />
                    <span className="text-xs text-gray-500">Inativos</span>
                  </label>
                  {filtros.colaborador_id.length > 0 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setFiltros({ ...filtros, colaborador_id: [] }); }}
                      className="text-xs text-red-500 hover:text-red-400"
                    >
                      Limpar
                    </button>
                  )}
                  {colabAberto ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                </div>
              </button>
              {colabAberto && (
                <div className="flex flex-wrap gap-2 max-h-[200px] overflow-y-auto p-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg border">
                  {colaboradoresFiltrados.map((u) => {
                    const isSelected = filtros.colaborador_id.includes(u.id);
                    const isFixado = vendedoresFixados.includes(u.id);
                    return (
                      <div
                        key={u.id}
                        className={`group flex items-center gap-1 pl-1 pr-2 py-1.5 rounded-full border text-xs font-medium transition-all ${
                          isSelected
                            ? 'bg-red-500 text-white border-red-500 shadow-md'
                            : u.ativo === false
                              ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 border-gray-200 dark:border-gray-600 hover:border-gray-400'
                              : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-600 hover:border-red-400 hover:shadow-sm'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleFixarVendedor(u.id, u.nome); }}
                          className={`p-0.5 rounded-full transition-colors shrink-0 ${isFixado ? (isSelected ? 'text-amber-200' : 'text-amber-500') : `${isSelected ? 'text-white/70' : 'text-gray-400'} opacity-0 group-hover:opacity-100`}`}
                          title={isFixado ? 'Desafixar vendedor' : 'Fixar vendedor no topo'}
                          data-testid={`pin-vendedor-${u.id}`}
                        >
                          <Pin className="h-3 w-3" fill={isFixado ? 'currentColor' : 'none'} />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setFiltros(prev => ({
                              ...prev,
                              colaborador_id: isSelected
                                ? prev.colaborador_id.filter(id => id !== u.id)
                                : [...prev.colaborador_id, u.id]
                            }));
                          }}
                          className="flex items-center gap-2"
                          data-testid={`colaborador-chip-${u.id}`}
                        >
                          <UserAvatar fotoPath={u.foto_path} nome={u.nome} size="xs" />
                          <span className="truncate max-w-[120px]">{u.nome}</span>
                          {u.ativo === false && <span className="text-[9px] opacity-60">(IN)</span>}
                        </button>
                      </div>
                    );
                  })}
                  {colaboradoresFiltrados.length === 0 && (
                    <p className="text-xs text-gray-400 py-2 px-1">Nenhum colaborador encontrado</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Busca Unificada - Cliente, CPF, CNPJ, Contrato, Proposta, Código */}
          <div className="grid grid-cols-1 gap-4 mb-6">
            <div>
              <Label className="text-red-600 text-sm">Pesquisar (cliente, nome, CPF, CNPJ, contrato, codigo, telefone)</Label>
              <Input
                className="mt-1"
                placeholder="Digite o nome, CPF, CNPJ, contrato, codigo ou telefone..."
                value={filtros.busca_geral || ''}
                onChange={(e) => setFiltros({ ...filtros, busca_geral: e.target.value })}
                data-testid="busca-geral-input"
              />
            </div>
          </div>

          {/* Botões */}
          <div className="flex gap-3">
            <Button 
              onClick={consultarVendas}
              className="bg-red-600 hover:bg-red-700"
              data-testid="consultar-btn"
            >
              <Search className="mr-2 h-4 w-4" />
              CONSULTAR
            </Button>
            <Button 
              onClick={handleExportar}
              className="bg-gray-700 hover:bg-gray-800"
            >
              <Download className="mr-2 h-4 w-4" />
              EXPORTAR
            </Button>
            <Button 
              onClick={limparFiltros}
              variant="outline"
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              LIMPAR
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabela de Vendas */}
      <Card>
        <CardHeader>
          <CardTitle>
            Vendas Encontradas: <span className="text-red-600">{vendasFiltradas.length}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="h-12 w-12 animate-spin mx-auto text-red-600" />
              <p className="text-gray-500 mt-4">Carregando vendas...</p>
            </div>
          ) : vendasFiltradas.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Nenhuma venda encontrada</p>
            </div>
          ) : (
            <>
            {/* Ordenar vendas por data de cadastro (mais recentes primeiro) */}
            {(() => {
              const vendasOrdenadas = [...vendasFiltradas].sort((a, b) => 
                new Date(b.criado_em) - new Date(a.criado_em)
              );
              
              const totalPaginas = Math.ceil(vendasOrdenadas.length / VENDAS_POR_PAGINA);
              const inicio = (paginaAtual - 1) * VENDAS_POR_PAGINA;
              const fim = inicio + VENDAS_POR_PAGINA;
              const vendasPaginadas = vendasOrdenadas.slice(inicio, fim);

              return (
                <>
                  {layoutMode === 'lista' ? (
                    /* LAYOUT LISTA */
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        {/* Header Principal */}
                        <thead className="bg-gradient-to-r from-red-600 via-purple-600 to-purple-700 text-white">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase border border-red-700">
                              Produtos Vendidos
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase border border-red-700">
                              Tipo
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase border border-red-700">
                              Código
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase border border-red-700">
                              Empresa
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase border border-red-700">
                              Contrato
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase border border-red-700">
                              Cidade/Distrito
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase border border-red-700">
                              Vendedor
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase border border-red-700">
                              Usuário Tratamento
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase border border-red-700">
                              Situação
                            </th>
                            <th className="px-3 py-2 text-center text-xs font-medium uppercase border border-red-700 w-16">
                              
                            </th>
                          </tr>
                        </thead>
                      <tbody>
                        {vendasPaginadas.map((venda) => (
                    <React.Fragment key={venda.id}>
                      {/* Linha Principal */}
                      <tr className="hover:bg-gray-50 border-b border-gray-200">
                        <td className="px-3 py-2 text-sm border border-gray-200 dark:border-gray-700">
                          <div className="max-w-xs truncate" title={getProdutosNomes(venda)}>
                            {getProdutosNomes(venda)}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-sm border border-gray-200 dark:border-gray-700">
                          <Badge className={
                            venda.tipo_venda === 'TT' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300' : 
                            ['Base', 'Residencial'].includes(venda.tipo_venda) ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' : 
                            ['Empresarial'].includes(venda.tipo_venda) ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' :
                            'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300'
                          }>
                            {venda.tipo_venda || 'Proposta'}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-sm font-mono text-red-600 font-semibold border border-gray-200 dark:border-gray-700">
                          <button 
                            onClick={() => navigate(`/vendas/${venda.id}`)}
                            className="hover:underline hover:text-red-800 cursor-pointer"
                          >
                            {venda.protocolo}
                          </button>
                          <CreditoVendaBadge credito={venda.credito} testid={`credito-badge-${venda.id}`} />
                        </td>
                        <td className="px-3 py-2 text-sm border border-gray-200 dark:border-gray-700">
                          <span className="font-medium text-gray-700">
                            {empresas.find(e => e.id === venda.empresa_id)?.nome || '-'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-sm border border-gray-200 dark:border-gray-700">
                          {mascararContrato(venda.contrato)}
                        </td>
                        <td className="px-3 py-2 text-sm border border-gray-200 dark:border-gray-700">
                          {(venda.endereco?.cidade || '-').toUpperCase()}/{venda.endereco?.estado || '-'}
                        </td>
                        <td className="px-3 py-2 text-sm border border-gray-200 dark:border-gray-700">
                          {venda.vendedor_nome}
                        </td>
                        <td className="px-3 py-2 text-sm border border-gray-200 dark:border-gray-700">
                          {venda.usuario_tratamento || <span className="text-gray-400 italic text-xs">-</span>}
                        </td>
                        <td className="px-3 py-2 border border-gray-200 dark:border-gray-700">
                          <Badge variant="outline" className="text-xs border-0" style={{ backgroundColor: statusCores[venda.status] || '#9ca3af', color: '#fff' }}>
                            {venda.status}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-center border border-gray-200 dark:border-gray-700">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleRow(venda.id)}
                            className="h-6 w-6 p-0"
                            data-testid={`btn-expandir-venda-${venda.id}`}
                          >
                            {expandedRows[venda.id] ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </Button>
                        </td>
                      </tr>

                      {/* Linha Expandida - Detalhes */}
                      {expandedRows[venda.id] && (
                        <>
                          {/* Alerta de Venda Bloqueada por Duplicidade */}
                          {venda.bloqueio_duplicidade?.bloqueado && (
                            <tr className="bg-amber-50">
                              <td colSpan={(isAdmin || isBKO) ? 11 : 10} className="px-4 py-3">
                                <div className="flex items-center gap-3 text-amber-800">
                                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                                  <div>
                                    <p className="font-semibold">⚠️ Venda bloqueada por duplicidade</p>
                                    <p className="text-sm">
                                      {venda.bloqueio_duplicidade.motivo}
                                    </p>
                                    <p className="text-xs mt-1">
                                      Aguardando autorização do ADMIN/BKO para liberar ou trocar vendedor.
                                    </p>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}

                          {/* Primeira linha de detalhes */}
                          <tr className="bg-gray-50">
                            <td colSpan={(isAdmin || isBKO) ? 11 : 10} className="px-0 py-0">
                              <div className="grid grid-cols-4 border-b border-gray-200">
                                <div className="px-4 py-3 border-r border-gray-200">
                                  <p className="text-xs text-red-600 font-medium uppercase">Cliente</p>
                                  <p className="text-sm font-semibold mt-1">{venda.cliente?.nome_razao_social}</p>
                                </div>
                                <div className="px-4 py-3 border-r border-gray-200">
                                  <p className="text-xs text-red-600 font-medium uppercase">CPF / CNPJ</p>
                                  <p className="text-sm font-semibold mt-1">{venda.cliente?.documento}</p>
                                </div>
                                <div className="px-4 py-3 border-r border-gray-200">
                                  <p className="text-xs text-red-600 font-medium uppercase">Tipo da Venda</p>
                                  <p className="text-sm font-semibold mt-1">{venda.tipo_venda}</p>
                                </div>
                                <div className="px-4 py-3">
                                  <p className="text-xs text-red-600 font-medium uppercase">Usuário de Bloqueio</p>
                                  <p className="text-sm font-semibold mt-1">
                                    {venda.bloqueio?.usuario_bloqueio || '-'}
                                  </p>
                                </div>
                              </div>
                            </td>
                          </tr>

                          {/* Segunda linha de detalhes */}
                          <tr className="bg-gray-50">
                            <td colSpan={(isAdmin || isBKO) ? 11 : 10} className="px-0 py-0">
                              <div className="grid grid-cols-4 border-b border-gray-300">
                                <div className="px-4 py-3 border-r border-gray-200">
                                  <p className="text-xs text-red-600 font-medium uppercase">Data da Proposta</p>
                                  <p className="text-sm font-semibold mt-1 text-red-600">
                                    {venda.criado_em 
                                      ? format(new Date(venda.criado_em), "dd/MM/yyyy HH:mm:ss", { locale: ptBR })
                                      : '-'
                                    }
                                  </p>
                                </div>
                                <div className="px-4 py-3 border-r border-gray-200">
                                  <p className="text-xs text-red-600 font-medium uppercase">Data de Tratamento</p>
                                  <p className="text-sm font-semibold mt-1">
                                    {venda.atualizado_em 
                                      ? format(new Date(venda.atualizado_em), "dd/MM/yyyy HH:mm:ss", { locale: ptBR })
                                      : '-'
                                    }
                                  </p>
                                </div>
                                <div className="px-4 py-3 border-r border-gray-200">
                                  <p className="text-xs text-red-600 font-medium uppercase">Usuário de Tratamento</p>
                                  <p className="text-sm font-semibold mt-1">
                                    {venda.bloqueio?.usuario_bloqueio || venda.vendedor_nome}
                                  </p>
                                </div>
                                <div className="px-4 py-3">
                                  <p className="text-xs text-red-600 font-medium uppercase">Valor do Plano</p>
                                  <p className="text-sm font-semibold mt-1 text-green-600">
                                    R$ {parseFloat(venda.valor_plano || 0).toFixed(2)}
                                  </p>
                                </div>
                              </div>
                            </td>
                          </tr>

                          {/* Terceira linha - Ações */}
                          <tr className="bg-gray-100">
                            <td colSpan={(isAdmin || isBKO) ? 11 : 10} className="px-4 py-2">
                              <div className="flex gap-2 justify-end">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => navigate(`/vendas/${venda.id}`)}
                                  className="text-xs"
                                >
                                  <Eye className="h-3 w-3 mr-1" />
                                  Detalhar
                                </Button>

                                {podeBloquear && !venda.bloqueio?.bloqueado && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleBloquear(venda.id)}
                                    className="text-xs"
                                  >
                                    <Lock className="h-3 w-3 mr-1" />
                                    Bloquear
                                  </Button>
                                )}

                                {podeBloquear && venda.bloqueio?.bloqueado && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDesbloquear(venda.id)}
                                    className="text-xs"
                                  >
                                    <Unlock className="h-3 w-3 mr-1" />
                                    Desbloquear
                                  </Button>
                                )}

                                {/* Botão Liberar Duplicidade (BKO/Admin) */}
                                {podeBloquear && venda.bloqueio_duplicidade?.bloqueado && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleLiberarDuplicidade(venda.id, venda.protocolo)}
                                    className="text-xs bg-green-50 border-green-300 text-green-700 hover:bg-green-100"
                                  >
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Liberar Venda
                                  </Button>
                                )}

                                {/* Botão Excluir (apenas ADMIN) */}
                                {isAdmin && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleExcluir(venda.id, venda.protocolo)}
                                    className="text-xs bg-red-50 border-red-300 text-red-700 hover:bg-red-100"
                                    data-testid={`btn-excluir-venda-${venda.id}`}
                                  >
                                    <Trash2 className="h-3 w-3 mr-1" />
                                    Excluir
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        </>
                      )}
                    </React.Fragment>
                  ))}
                      </tbody>
                    </table>
                  </div>
                  ) : (
                    /* LAYOUT GRID / BLOCOS */
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {vendasPaginadas.map((venda) => (
                        <div
                          key={venda.id}
                          className={`bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden ${
                            venda.bloqueio_duplicidade?.bloqueado ? 'border-amber-400 border-2' : 'border-gray-200'
                          }`}
                        >
                          {/* Header do Card */}
                          <div className="px-4 py-3 rounded-t-lg" style={{ backgroundColor: statusCores[venda.status] || '#9ca3af', color: '#fff' }}>
                            <div className="flex items-center justify-between">
                              <span className="font-bold text-sm">{venda.protocolo}</span>
                              <Badge variant="outline" className="text-xs border-0" style={{ backgroundColor: statusCores[venda.status] || '#9ca3af', color: '#fff' }}>
                                {venda.status}
                              </Badge>
                            </div>
                          </div>

                          {/* Corpo do Card */}
                          <div className="p-4 space-y-3">
                            {/* Cliente */}
                            <div>
                              <p className="text-xs text-gray-500 uppercase">Cliente</p>
                              <p className="font-semibold text-gray-900 truncate">{venda.cliente?.nome_razao_social || '-'}</p>
                              <p className="text-xs text-gray-600">{venda.cliente?.documento}</p>
                              <CreditoVendaBadge credito={venda.credito} testid={`credito-badge-card-${venda.id}`} />
                            </div>

                            {/* Produtos */}
                            <div>
                              <p className="text-xs text-gray-500 uppercase">Produtos</p>
                              <p className="text-sm text-gray-700 truncate">{getProdutosNomes(venda)}</p>
                            </div>

                            {/* Info Grid */}
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <p className="text-xs text-gray-500">Cidade/UF</p>
                                <p className="text-gray-700 truncate">{(venda.endereco?.cidade || '-').toUpperCase()}/{venda.endereco?.estado || '-'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Vendedor</p>
                                <p className="text-gray-700 truncate">{venda.vendedor_nome}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Contrato</p>
                                <p className="text-gray-700">{mascararContrato(venda.contrato)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Data</p>
                                <p className="text-gray-700">
                                  {venda.criado_em ? format(new Date(venda.criado_em), "dd/MM/yy", { locale: ptBR }) : '-'}
                                </p>
                              </div>
                            </div>

                            {/* Alerta de Duplicidade */}
                            {venda.bloqueio_duplicidade?.bloqueado && (
                              <div className="bg-amber-50 border border-amber-200 rounded p-2 flex items-center gap-2">
                                <AlertTriangle className="h-4 w-4 text-amber-600" />
                                <span className="text-xs text-amber-700">Bloqueada por duplicidade</span>
                              </div>
                            )}
                          </div>

                          {/* Footer do Card - Ações */}
                          <div className="px-4 py-3 bg-gray-50 border-t flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/vendas/${venda.id}`)}
                              className="text-xs flex-1"
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              Detalhar
                            </Button>
                            {podeBloquear && venda.bloqueio_duplicidade?.bloqueado && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleLiberarDuplicidade(venda.id, venda.protocolo)}
                                className="text-xs bg-green-50 border-green-300 text-green-700"
                              >
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Liberar
                              </Button>
                            )}
                            {isAdmin && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleExcluir(venda.id, venda.protocolo)}
                                className="text-xs bg-red-50 border-red-300 text-red-700"
                                data-testid={`btn-excluir-venda-card-${venda.id}`}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Paginação */}
                  {totalPaginas > 1 && (
                    <div className="flex items-center justify-between mt-6 pt-4 border-t">
                      <div className="text-sm text-gray-600">
                        Mostrando <span className="font-semibold">{inicio + 1}</span> a{' '}
                        <span className="font-semibold">{Math.min(fim, vendasOrdenadas.length)}</span> de{' '}
                        <span className="font-semibold">{vendasOrdenadas.length}</span> vendas
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {/* Botão Anterior */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPaginaAtual(prev => Math.max(prev - 1, 1))}
                          disabled={paginaAtual === 1}
                          className="flex items-center gap-1"
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Anterior
                        </Button>

                        {/* Números das páginas */}
                        <div className="flex items-center gap-1">
                          {Array.from({ length: totalPaginas }, (_, i) => i + 1).map((pagina) => {
                            // Mostrar apenas páginas próximas à atual
                            if (
                              pagina === 1 ||
                              pagina === totalPaginas ||
                              (pagina >= paginaAtual - 2 && pagina <= paginaAtual + 2)
                            ) {
                              return (
                                <Button
                                  key={pagina}
                                  variant={pagina === paginaAtual ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => setPaginaAtual(pagina)}
                                  className={`w-9 h-9 ${pagina === paginaAtual ? 'bg-red-600 hover:bg-red-700' : ''}`}
                                >
                                  {pagina}
                                </Button>
                              );
                            } else if (
                              pagina === paginaAtual - 3 ||
                              pagina === paginaAtual + 3
                            ) {
                              return <span key={pagina} className="px-1 text-gray-400">...</span>;
                            }
                            return null;
                          })}
                        </div>

                        {/* Botão Próxima */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPaginaAtual(prev => Math.min(prev + 1, totalPaginas))}
                          disabled={paginaAtual === totalPaginas}
                          className="flex items-center gap-1"
                        >
                          Próxima
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
            </>
          )}
        </CardContent>
      </Card>

      <ConfirmarSenhaExclusao
        aberto={!!exclusaoPendente}
        protocolo={exclusaoPendente?.protocolo}
        excluindo={excluindo}
        onConfirmar={confirmarExclusao}
        onCancelar={() => setExclusaoPendente(null)}
      />
    </div>
  );
};

export default Vendas;
